import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import AnimatedBox from 'modules/attendance/components/ClockIn/EmployeeTimeTrackingBreakdown/components/AnimatedBox'
import { getOvertimeWorkedTimeBlocksByCategory } from 'modules/attendance/components/ClockIn/EmployeeTimeTrackingBreakdown/lib/getOvertimeWorkedTimeBlocksByCategory'
import { useBalanceRange } from 'modules/attendance/components/ClockIn/EmployeeTimeTrackingBreakdown/lib/useBalanceRange'
import { OvertimePoolsCollection } from 'modules/attendance/hooks/OvertimePoolsCollection'
import { useAttendanceDateNavigator } from 'modules/attendance/lib/attendanceDateNavigatorContext'
import { useTimeSettingsCategory } from 'modules/attendance/lib/timeSettingsCategoryContext'
import { AggregatedWorkedTimeBlocks } from 'modules/attendance/lib/utils/AttendanceWorkedTimeUtils'
import { TimeBlocksBalanceCollection } from 'modules/attendance/store/models/TimeBlocksBalanceCollection'

import AccruedSelectedMonth from './AccruedSelectedMonth'
import Discarded from './Discarded'
import Footer from './Footer'
import Header from './Header'
import ReservedForTimeOff from './ReservedForTimeOff'

interface Props {
  overtimePools: OvertimePoolsCollection
  timeBlocksBalance: TimeBlocksBalanceCollection
  timeoffCompensationEnabled: boolean
  payrollCompensationEnabled: boolean
  workedTimeForEmployee?: AggregatedWorkedTimeBlocks | null
}

const Balance = ({
  overtimePools,
  timeBlocksBalance,
  timeoffCompensationEnabled,
  payrollCompensationEnabled,
  workedTimeForEmployee,
}: Props) => {
  const [openAccrued, setOpenAccrued] = React.useState(false)
  const { stringDateTo: endOn, cycle } = useAttendanceDateNavigator()
  const { timeSettingsCategories } = useTimeSettingsCategory()

  const {
    balancePoolMinutes,
    balancePoolTransfersMinutes,
    timeoffPoolTransfersMinutes,
    discardedPoolTransfersMinutes,
    negativeBalance,
  } = timeBlocksBalance

  const accruedSelectedMonth = cycle === 'monthly'
  const workedTimeBlocks = workedTimeForEmployee?.workedTimeBlocks ?? []
  const blocksUntilEndOn = workedTimeBlocks.filter((block) => block.date <= endOn)

  const { categoryNames } = getOvertimeWorkedTimeBlocksByCategory(
    blocksUntilEndOn,
    timeSettingsCategories
  )

  const balancePoolCapacity = overtimePools.getBalancePoolCapacity()
  const withCompensationEnabled = timeoffCompensationEnabled || payrollCompensationEnabled
  const hasDiscardedPoolTransfers = discardedPoolTransfersMinutes > 0
  const showBreakdown = timeoffCompensationEnabled || hasDiscardedPoolTransfers

  const selectedMonth = moment(endOn).startOf('month')
  const { balanceRangeStartOn, balanceRangeEndOn } = useBalanceRange(
    timeBlocksBalance.startOn,
    timeBlocksBalance.endOn
  )

  if (negativeBalance || !withCompensationEnabled) {
    return (
      <Box gap="s16" paddingX="s24">
        <Header
          balancePoolMinutes={balancePoolMinutes}
          balancePoolCapacity={balancePoolCapacity}
          startOn={balanceRangeStartOn}
          endOn={balanceRangeEndOn}
        />
        <Footer categoryNames={categoryNames} />
      </Box>
    )
  }

  return (
    <Box gap="s16" paddingX="s24">
      <Header
        balancePoolMinutes={balancePoolMinutes}
        balancePoolCapacity={balancePoolCapacity}
        startOn={balanceRangeStartOn}
        endOn={balanceRangeEndOn}
      />
      {accruedSelectedMonth && (
        <Box gap="s4">
          <AccruedSelectedMonth
            openAccrued={openAccrued}
            setOpenAccrued={setOpenAccrued}
            showBreakdown={showBreakdown}
            date={selectedMonth}
            minutes={balancePoolTransfersMinutes}
          />
          {showBreakdown && (
            <AnimatedBox isOpen={openAccrued}>
              <Box paddingLeft="s24" gap="s4">
                {timeoffCompensationEnabled && (
                  <ReservedForTimeOff
                    totalTimeOffPoolsMinutes={
                      timeoffPoolTransfersMinutes - discardedPoolTransfersMinutes
                    }
                  />
                )}
                {hasDiscardedPoolTransfers && (
                  <Discarded discardedPoolMinutes={discardedPoolTransfersMinutes} />
                )}
              </Box>
            </AnimatedBox>
          )}
        </Box>
      )}
      <Footer categoryNames={categoryNames} />
    </Box>
  )
}

export default Balance
